import { useMemo } from 'react';

import { useColumnQualityDataQuery, useTableQualityDataQuery } from '@/entities/quality/model';

import {
  getColumnQualityColumns,
  getTableQualityColumns,
  transformColumnApiDataToTableData,
  transformTableApiDataToTableData,
} from '@/features/quality/lib';

import type { AssetQualityTableResult, AssetQualityTabValue } from './types';
import { useAssetQualityApiParams } from './useAssetQualityApiParams';
import { useQualityPaginationConfig } from './useQualityPaginationConfig';

type UseAssetQualityTableParams = {
  tabValue: AssetQualityTabValue;
};

export const useAssetQualityTable = ({ tabValue }: UseAssetQualityTableParams): AssetQualityTableResult => {
  // 임시 total 값으로 페이지네이션 설정 - 실제 total은 API 응답 후 paginationConfig에서 갱신됨
  const { page, pageSize, paginationConfig } = useQualityPaginationConfig(0);

  const isColumnTab = tabValue === 'column';
  const apiParams = useAssetQualityApiParams({ tabValue, page, pageSize });

  const { data: tableApiResponse, isLoading: isTableLoading } = useTableQualityDataQuery(apiParams, !isColumnTab);
  const { data: columnApiResponse, isLoading: isColumnLoading } = useColumnQualityDataQuery(apiParams, isColumnTab);

  const columnColumns = useMemo(() => getColumnQualityColumns(), []);
  const tableColumns = useMemo(() => getTableQualityColumns(), []);

  const columnTransformedData = useMemo(
    () => columnApiResponse?.data?.map(transformColumnApiDataToTableData) ?? [],
    [columnApiResponse]
  );

  const tableTransformedData = useMemo(
    () => tableApiResponse?.data?.map(transformTableApiDataToTableData) ?? [],
    [tableApiResponse]
  );

  const total = isColumnTab ? (columnApiResponse?.meta.total ?? 0) : (tableApiResponse?.meta.total ?? 0);
  const paginationConfigWithTotal = useMemo(() => ({ ...paginationConfig, total }), [paginationConfig, total]);

  if (isColumnTab) {
    return {
      type: 'column',
      columns: columnColumns,
      data: columnTransformedData,
      isLoading: isColumnLoading,
      paginationConfig: paginationConfigWithTotal,
    };
  }

  return {
    type: 'table',
    columns: tableColumns,
    data: tableTransformedData,
    isLoading: isTableLoading,
    paginationConfig: paginationConfigWithTotal,
  };
};
